import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { ArrowCollapsed } from '@sas/react-svg';
import cx from 'classnames';
import { useClosestAttribute } from '../../hooks/useClosestAttribute';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../hooks/useKeyboardTooltip';
import { useRefArray } from '../../hooks/useRefArray';
import { isTruncated, useTruncationTooltip } from '../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { Icon } from '../Icon';
import { Indicator } from '../Indicator';
import { InvisibleText } from '../InvisibleText';
import { Link } from '../Link';
import { Text } from '../Text';
import { DraggableSection, DroppableZeroState, SectionDragPreview } from './__internal__';
import bundle from './NovaComponents-WindowShade-gui-icu.properties';
import cn from './WindowShade.css.json';
import './WindowShade.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useWindowShadeDragType = createUseHtmlId('WindowShade-dragType');
const useWindowShadeDndPrefix = createUseHtmlId('WindowShade-dndPrefix');
const useWindowShadeSectionStateId = createUseHtmlId('Section-state');
const logger = getLogger('WindowShade');
const nestedHeadingLevelMap = {
  '': '4',
  '4': '5',
  '5': '6',
  '6': '6'
};
export const BaseWindowShade = props => {
  const {
    setSharedHeaderRefs
  } = props;
  const {
    refs: headerRefs,
    createSetRef: createHeaderRef
  } = useRefArray();
  const dt = useDesignTokens();
  const defaultDndPrefix = useWindowShadeDndPrefix();
  const dndPrefix = props.dndPrefix ?? defaultDndPrefix;
  const defaultDragType = useWindowShadeDragType();
  const dragType = props.dragType ?? defaultDragType;
  const allowDropTypes = [...new Set([dragType, ...(props.allowDropTypes ?? [])])];
  const allowDrop = props.allowDrop ?? false;
  const containerRef = useRef(null);
  const showDragPreview = useMemo(() => props.sections.some(item => !!item.allowDrag), [props.sections]);
  const [headingLevelAttr, setContainerRef] = useClosestAttribute('data-ws-heading-level');
  const headingLevel = nestedHeadingLevelMap[headingLevelAttr];
  const defaultSectionsLabel = props.sections.slice(0, 2).map(section => section.header).join(', ') + (props.sections.length > 2 ? ', …' : '');
  useEffect(() => {
    if (headingLevelAttr === '6') logger.error('Only three levels of Window Shades are allowed. Additional levels will cause an accessibility WCAG violation as well as usability issues. Please re-evaluate your design. If you still need additional levels, contact the Filament Nova team for a new feature.');
  }, [headingLevelAttr]);
  useEffect(() => {
    if (setSharedHeaderRefs) {
      const sharedRefs = headerRefs.filter(ref => ref !== null);
      setSharedHeaderRefs(sharedRefs);
    }
  }, [headerRefs, setSharedHeaderRefs]);
  const getOnSectionKeyDown = index => event => {
    if (event.key === 'Enter' || event.key === ' ') {
      props.onToggleSection(index);
      event.preventDefault();
      return;
    }
    const allHeadersRefs = props.allHeadersRefs?.length ? props.allHeadersRefs : headerRefs;
    if (!allHeadersRefs.includes(event.target)) {
      return;
    }
    if (event.key === 'Home') {
      allHeadersRefs[0]?.focus();
    } else if (event.key === 'End') {
      allHeadersRefs[allHeadersRefs?.length - 1]?.focus();
    } else if (event.key === 'ArrowDown') {
      const index = allHeadersRefs.indexOf(event.target);
      // NOTE: bounds check doesn't matter here
      allHeadersRefs[index + 1]?.focus();
    } else if (event.key === 'ArrowUp') {
      const index = allHeadersRefs.indexOf(event.target);
      // NOTE: bounds check doesn't matter here
      allHeadersRefs[index - 1]?.focus();
    } else {
      // No expected input was hit, so stop preventDefault
      return;
    }
    event.preventDefault();
  };
  const getOnSectionHeaderSelect = index => () => props.onToggleSection(index);
  function renderDraggableWindowShadeSection(index, sectionProps, content) {
    const {
      allowDrag = false
    } = sectionProps;
    return /*#__PURE__*/_jsx(DraggableSection, {
      index: index,
      dragType: dragType,
      allowDrag: allowDrag,
      allowDrop: allowDrop,
      dndPrefix: dndPrefix,
      allowDropTypes: allowDropTypes,
      onDrop: props.onDrop,
      onSectionsReorder: props.onSectionsReorder,
      isLastSection: index === props.sections.length - 1,
      children: dndOptions => renderWindowShadeSection(index, sectionProps, content, {
        ...dndOptions
      })
    }, index);
  }
  function renderWindowShadeSection(index, sectionProps, content, dndOptions = {}) {
    const {
      draggingClassName,
      isDragImage,
      dragRef
    } = dndOptions;
    return /*#__PURE__*/_jsx(WindowShadeSection, {
      ...sectionProps,
      dragRef: dragRef,
      isDragImage: isDragImage,
      draggingClassName: draggingClassName,
      children: content
    }, index);
  }
  const getSectionProps = ({
    section,
    index,
    isDragImage
  }) => {
    const {
      theme,
      header,
      headerCount,
      content,
      disabled,
      required,
      staticMode,
      maxHeight,
      allowDrag,
      headingIcon,
      headingStyle,
      headerAction,
      state,
      count,
      renderToolbar,
      headingClassName
    } = section;
    return {
      theme,
      header,
      headerCount,
      content,
      disabled,
      required,
      staticMode: staticMode ?? props.staticMode,
      maxHeight,
      allowDrag,
      headingIcon,
      headingStyle,
      headerAction,
      state,
      count,
      renderToolbar,
      headingClassName,
      variant: props.variant,
      wrapHeaderText: props.wrapHeaderText,
      showHeadingIcon: props.showHeadingIcon,
      isOpen: props.openSections.includes(index),
      headerRef: createHeaderRef(index),
      onKeyDown: getOnSectionKeyDown(index),
      onSelectHeader: getOnSectionHeaderSelect(index),
      testId: postfixTestId(props.testId, `section-${index}`),
      headingLevel,
      ...(!!isDragImage && {
        isOpen: false,
        headerRef: null,
        allowDrag: false,
        onKeyDown: () => {},
        onSelectHeader: () => {},
        variant: props.variant,
        wrapHeaderText: props.wrapHeaderText,
        showHeadingIcon: props.showHeadingIcon,
        testId: postfixTestId(props.testId, `section-${index}-ghost-image`)
      })
    };
  };
  function renderSectionsContainer() {
    const tokens = {
      ...getWindowShadeDesignTokens(dt),
      ...props.theme?.(dt)
    };
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsxs("div", {
        ref: element => {
          containerRef.current = element;
          setContainerRef(element);
        },
        className: cx(cn.container, props.className),
        style: {
          ...tokens,
          ...props.style
        },
        "data-testid": props.testId,
        role: props.role,
        "data-landmark-label": props.landmarkLabel ?? defaultSectionsLabel,
        "aria-label": props.ariaLabel ?? defaultSectionsLabel,
        children: [props.sections.map((section, index) => {
          const {
            content,
            ...baseSectionProps
          } = getSectionProps({
            section,
            index
          });
          const sectionProps = {
            ...baseSectionProps,
            isLastSection: index === props.sections.length - 1
          };
          const {
            allowDrag
          } = sectionProps;
          return allowDrag || allowDrop ? renderDraggableWindowShadeSection(index, sectionProps, content) : renderWindowShadeSection(index, sectionProps, content);
        }), allowDrop && props.sections.length === 0 && /*#__PURE__*/_jsx(DroppableZeroState, {
          dragType: dragType,
          dndPrefix: dndPrefix,
          onDrop: props.onDrop,
          allowDropTypes: allowDropTypes,
          testId: postfixTestId(props.testId, 'droppable-zeroState')
        })]
      }), showDragPreview && /*#__PURE__*/_jsx(SectionDragPreview, {
        ref: element => {
          if (element && containerRef.current) {
            element.style.width = `${containerRef.current.offsetWidth}px`;
          }
        },
        dragType: dragType,
        dndPrefix: dndPrefix,
        style: {
          ...tokens
        },
        children: index => {
          const {
            content,
            ...sectionProps
          } = getSectionProps({
            section: props.sections[index],
            index,
            isDragImage: true
          });
          return renderWindowShadeSection(index, sectionProps, content, {
            isDragImage: true
          });
        }
      })]
    });
  }
  return renderSectionsContainer();
};
const useHtmlId = createUseHtmlId('WindowShadeSection');
const WindowShadeSection = props => {
  const contentSection = useRef(null);
  const sectionRef = useRef(null);
  const localizer = useTextLocalizer();
  const {
    0: windowShadeSectionId,
    1: sectionHeaderBtnId,
    2: contentPanelId
  } = useHtmlId(3);
  const CustomHeading = `h${props.headingLevel ?? '4'}`;
  const [{
    isOpen,
    sectionTransitionProps,
    iconTransitionProps
  }, setTransition] = useState({
    // NOTE: we initialize to props.isOpen to avoid transitioning on mount.
    isOpen: props.isOpen
  });
  const getAnimationDuration = height => {
    /** rate = 16 pixels per 25ms */
    const rate = 1 / 16 * 25;
    return Math.max(Math.min(height * rate, 400), 100);
  };
  useEffect(() => {
    if (props.isOpen === isOpen || !contentSection.current) return;
    let sectionStyle;
    let iconStyle;

    // NOTE: This animation code is close to the "FLIP" technique (except we don't use
    //     | transform, because this needs to affect layout during the transition).
    //     | ref: https://aerotwist.com/blog/flip-your-animations/
    if (props.isOpen) {
      // Transition from Closed => Open
      // measure the size of the content (we can't transition a height to/from 'auto')
      contentSection.current.style.height = 'auto';
      contentSection.current.style.display = '';
      // NOTE: reading clientHeight causes the browser to do layout (synchronously)
      const height = contentSection.current.clientHeight;
      // close the section back up, so we can start the transition on the next react render
      contentSection.current.style.height = '0px';
      // another synchronous layout for the browser. We use a function call instead of
      // reading a property to avoid issues with aggressive JS minifiers.
      contentSection.current.getBoundingClientRect();
      const transitionDuration = `calc(${getAnimationDuration(height)}ms * var(--nova-animation-duration-factor, 1))`;
      sectionStyle = {
        height,
        transitionDuration
      };
      iconStyle = {
        transitionDuration
      };
    } else {
      // Transition from Open => Closed
      const currentHeight = contentSection.current.clientHeight;
      // we can't transition from height="auto", so we set the size
      contentSection.current.style.height = `${currentHeight}px`;
      // This is a synchronous layout so the height mutation takes affect. We use a function call
      // instead of reading a property to avoid issues with aggressive JS minifiers.
      contentSection.current.getBoundingClientRect();
      const transitionDuration = `calc(${getAnimationDuration(currentHeight)}ms * var(--nova-animation-duration-factor, 1))`;
      sectionStyle = {
        height: 0,
        transitionDuration,
        display: 'none'
      };
      iconStyle = {
        transitionDuration
      };
    }

    // NOTE: we use one setState call here to avoid multiple renders in Legacy React (before concurrent/batched).
    // NOTE: in concurrent react, this set call may need to explicitly have the highest priority possible
    //     | (higher priority updates that interrupt this render may cause transitions to snap).
    setTransition({
      isOpen: props.isOpen,
      sectionTransitionProps: {
        className: cn.contentTransitioning,
        style: sectionStyle
      },
      iconTransitionProps: {
        className: cn.headerIconTransitioning,
        style: iconStyle
      }
    });
  }, [isOpen, props.isOpen]);
  const requiredSymbol = '*';
  let headerAction;
  if (props.renderToolbar) {
    headerAction = props.renderToolbar();
  } else if (props.headerAction?.type === 'button') {
    headerAction = /*#__PURE__*/_jsx(Button, {
      testId: postfixTestId(props.testId, 'action-button'),
      variant: ButtonVariant.Icon,
      disabled: props.headerAction.disabled ?? props.disabled,
      onPress: props.headerAction.onPress,
      icon: {
        icon: props.headerAction.iconType
      }
    });
  } else if (props.headerAction?.type === 'link') {
    headerAction = /*#__PURE__*/_jsx(Link, {
      testId: postfixTestId(props.testId, 'action-link'),
      wrapText: false,
      small: true,
      disabled: props.headerAction.disabled ?? props.disabled,
      onPress: props.headerAction.onPress,
      children: props.headerAction.text
    });
  }
  const state = props.state;
  const stateArray = state !== undefined && state !== null && !Array.isArray(state) ? [state] : state ?? [];
  const stateSectionIds = useWindowShadeSectionStateId(stateArray.length);
  const ariaDescribedBy = [];
  if (stateArray.length > 0) {
    ariaDescribedBy.splice(0, 0, ...stateSectionIds);
  }
  if (props.required) {
    ariaDescribedBy.push(`${windowShadeSectionId}-required`);
  }
  const count = props.count;
  const getKeyBoardTooltip = () => {
    let tooltip = '';
    if (textRef.current && isTruncated(textRef.current)) {
      if (props.showHeadingIcon && props.headingIcon?.tooltip) {
        tooltip = `${props.headingIcon.tooltip}, `;
      }
      tooltip += props.header;
    }
    return tooltip;
  };
  const textRef = useRef(null);
  const setTruncationTooltipRef = useTruncationTooltip();
  const setKeyBoardTooltipRef = useKeyboardTooltip({
    getTooltip: getKeyBoardTooltip
  });
  const setHeaderRef = useCallback(element => {
    setKeyBoardTooltipRef(element);
    setRef(props.headerRef, element);
  }, [props.headerRef, setKeyBoardTooltipRef]);
  const setTextRef = useCallback(element => {
    setTruncationTooltipRef(element);
    textRef.current = element;
  }, [setTruncationTooltipRef]);
  const dt = useDesignTokens();
  const tokens = {
    ...getWindowShadeSectionDesignTokens(dt, {
      disabled: props.disabled,
      variant: props.variant,
      showHeadingIcon: props.showHeadingIcon,
      headingIcon: props.headingIcon,
      state
    }),
    ...props.theme?.(dt)
  };
  const isExpandedRef = useRef(false);
  useEffect(() => {
    if (!props.isLastSection) return;
    if (isExpandedRef.current && props.isOpen && sectionRef.current) {
      sectionRef.current.scrollIntoView?.({
        block: 'nearest'
      });
    }
    isExpandedRef.current = false;
  }, [props.isLastSection, props.isOpen]);
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.section, props.className),
    "data-testid": props.testId,
    style: {
      ...props.style,
      ...tokens
    },
    ref: sectionRef,
    tabIndex: undefined,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: props.dragRef,
      className: cx(cn.sectionHeader, props.draggingClassName),
      tabIndex: undefined,
      children: [/*#__PURE__*/_jsx("div", {
        className: cn.headingLayout,
        children: /*#__PURE__*/_jsx(CustomHeading, {
          className: cn.heading,
          children: /*#__PURE__*/_jsxs("div", {
            className: cx(cn.header, props.wrapHeaderText && cn.headerWrap, props.staticMode && cn.arrowCursor),
            ref: setHeaderRef,
            onClick: e => {
              props.onSelectHeader?.(e);
              if (props.isLastSection) isExpandedRef.current = true;
            },
            onKeyDown: e => {
              props.onKeyDown?.(e);
              if (props.isLastSection) isExpandedRef.current = true;
            }
            // This is a button role to match UI5
            // FIXME: This role probably doesn't make sense for Accordions?
            ,
            ...(!props.staticMode && {
              role: 'button',
              'aria-expanded': props.isOpen
            }),
            "aria-describedby": ariaDescribedBy.join(' '),
            tabIndex: props.staticMode ? -1 : 0,
            "data-testid": postfixTestId(props.testId, 'header'),
            id: sectionHeaderBtnId,
            "aria-controls": !props.staticMode ? contentPanelId : undefined,
            children: [/*#__PURE__*/_jsx(Icon, {
              testId: postfixTestId(props.testId, 'icon'),
              className: cx(cn.headerIcon, iconTransitionProps?.className, isOpen ? cn.headerIconOpen : cn.headerIconClosed),
              style: {
                ...iconTransitionProps?.style
              },
              icon: ArrowCollapsed,
              disabled: props.staticMode ?? false,
              theme: dt => {
                if (props.variant === 'primary-light' && dt.themeVariant === 'light') {
                  return {
                    '--icon-default-override-fg': dt.themeVariant === 'light' ? dt.tokens['color-action-icon-emphasis-normal'] : dt.tokens['color-action-icon-standard-normal']
                  };
                }
              }
            }), props.showHeadingIcon && props.headingIcon && /*#__PURE__*/_jsx(Icon, {
              testId: postfixTestId(props.testId, 'optionalIcon'),
              className: cn.headerOptionalIcon,
              icon: props.headingIcon.icon,
              color: props.headingIcon.color,
              tooltip: props.headingIcon.tooltip ?? '',
              ariaLabel: props.headingIcon.ariaLabel ?? '',
              theme: props.headingIcon.theme
            }), stateArray.map((element, index) => {
              return /*#__PURE__*/_jsxs(Fragment, {
                children: [/*#__PURE__*/_jsx(Indicator, {
                  type: element,
                  count: element === 'count' ? count : undefined,
                  className: cx(element === 'count' ? !props.showHeadingIcon || !props.headingIcon ? index === 0 ? cn.countIndicatorNoIcon : cn.countIndicatorMultiState : index === 0 ? cn.countIndicator : cn.countIndicatorMultiState : !props.showHeadingIcon || !props.headingIcon ? index === 0 ? cn.indicatorNoIcon : cn.indicatorMultiState : index === 0 ? cn.indicator : cn.indicatorMultiState),
                  testId: postfixTestId(props.testId, `state-${element}`)
                }, element), /*#__PURE__*/_jsx(InvisibleText, {
                  id: stateSectionIds[index],
                  children: element === 'count' ? `${count}` : localizer(`shade.${element}.ariaLabel.aria`)
                })]
              }, element);
            }), /*#__PURE__*/_jsx(Text, {
              ref: setTextRef,
              testId: postfixTestId(props.testId, 'heading'),
              wrapText: !!props.wrapHeaderText,
              className: cx(cn.headerText, props.headingClassName, props.disabled && cn.disabled),
              style: props.headingStyle,
              children: props.header
            }), props.headerCount && /*#__PURE__*/_jsx(Text, {
              wrapText: false,
              testId: postfixTestId(props.testId, 'headingCount'),
              className: cx(cn.headerCount, props.disabled && cn.disabled),
              children: props.headerCount
            }), props.required && /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx("span", {
                className: cn.requiredMark,
                "aria-hidden": true,
                children: requiredSymbol
              }), /*#__PURE__*/_jsx(InvisibleText, {
                id: `${windowShadeSectionId}-required`,
                children: localizer('header.required.aria')
              })]
            })]
          })
        })
      }), headerAction && /*#__PURE__*/_jsx("div", {
        className: cn.headerAction,
        "aria-disabled": props.disabled,
        children: headerAction
      })]
    }), !props.isDragImage && /*#__PURE__*/_jsx("section", {
      className: cx(cn.content, sectionTransitionProps?.className),
      style: {
        display: isOpen ? undefined : 'none',
        height: isOpen ? 'auto' : 0,
        maxHeight: props.maxHeight,
        overflowY: props.maxHeight ? 'auto' : 'hidden',
        ...sectionTransitionProps?.style
      },
      onTransitionEnd: event => {
        if (event.target === event.currentTarget) {
          setTransition({
            isOpen: props.isOpen
          });
        }
      },
      ref: contentSection,
      "data-testid": postfixTestId(props.testId, 'content'),
      id: contentPanelId,
      "aria-labelledby": sectionHeaderBtnId,
      "aria-disabled": props.disabled,
      children: /*#__PURE__*/_jsx("div", {
        className: cn.sectionContainer,
        "data-ws-heading-level": props.headingLevel,
        children: props.children
      })
    })]
  });
};
export const getWindowShadeDesignTokens = dt => {
  return {
    ...renderDesignTokens({
      'spacing-m': dt.tokens['space-base-m'],
      'spacing-xxs': dt.tokens['space-base-xxs'],
      'spacing-s': dt.tokens['space-base-s'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'hoverState-color': dt.themeVariant === 'high-contrast' || dt.themeVariant === 'dark' ? dt.tokens['color-action-background-standard-hover'] : dt.tokens['color-action-background-control-hover'],
      'medium-sh': dt.tokens['shadow-depth-m'],
      'trans-dur': dt.tokens['duration-400'],
      'anim-dur': dt.tokens['duration-100'],
      'dragged-opacity': 0.3,
      'indicator-line-bg': dt.tokens['color-primary'],
      'indicator-loop': dt.tokens['color-text-neutral-inverse'],
      'font-size-h5': dt.tokens['font-size-heading-text-5'],
      'font-weight-default': dt.tokens['font-style-weight-400']
    }, 'windowshade'),
    ...(!dt.enableNovaTokens && getWindowShadeLegacyDesignTokens(dt))
  };
};
export const getWindowShadeLegacyDesignTokens = dt => {
  return renderDesignTokens({
    'hoverState-color': dt.themeVariant === 'high-contrast' || dt.themeVariant === 'dark' ? dt.reportTokens.backgrounds.page.bg : dt.reportTokens.backgroundsDepth.standard.bg,
    'medium-sh': dt.reportTokens.backgroundsDepth.medium.sh,
    'trans-dur': dt.tokens['duration-500'],
    'anim-dur': dt.tokens['duration-100'],
    'indicator-line-bg': dt.c.primary,
    'indicator-loop': dt.themeVariant === 'light' ? dt.c.white : dt.c.black,
    'font-size-h5': dt.reportTokens.font.size.h5,
    'font-weight-default': dt.reportTokens.font.weight.default
  }, 'windowshade');
};
export const getWindowShadeSectionDesignTokens = (dt, opts) => {
  const tokens = opts?.disabled ? {
    bg: dt.tokens['color-action-background-control-disabled'],
    bd: dt.tokens['color-action-border-control-disabled']
  } : {
    bg: dt.tokens['color-action-background-control-normal'],
    bd: dt.tokens['color-action-border-control-normal']
  };
  let focusTokens = 'var(--focus-light-bg)';
  let fgToken = dt.tokens['color-action-text-control-normal'];
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    fgToken = dt.tokens['color-text-neutral-inverse'];
    focusTokens = 'var(--focus-dark-bg)';
  }
  return {
    ...renderDesignTokens({
      ...tokens,
      fg: fgToken,
      'asterisk-fg': opts?.disabled ? dt.tokens['color-action-icon-control-disabled'] : dt.tokens['color-status-alert'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      focus: focusTokens
    }, 'windowshade-header'),
    ...renderDesignTokens({
      'padding-start': opts?.showHeadingIcon && opts?.headingIcon || !!opts?.state && opts.state.length > 0 ? 'var(--windowshade-spacing-xs)' : 'var(--windowshade-spacing-m)'
    }, 'windowshade'),
    ...(!dt.enableNovaTokens && getWindowShadeLegacyDesignTokens(dt))
  };
};
export const getWindowShadeSectionLegacyDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      'padding-start': opts?.showHeadingIcon && opts?.headingIcon || !!opts?.state && opts.state.length > 0 ? 'var(--windowshade-spacing-xs)' : 'var(--windowshade-spacing-m)'
    }, 'windowshade'),
    ...renderDesignTokens({
      ...(opts?.disabled ? dt.reportTokens.stateStandardContent.disabled : dt.reportTokens.stateStandardContent.normal),
      'asterisk-fg': opts?.disabled ? dt.reportTokens.stateStandard.disabled.fg : dt.c.alert,
      'font-size-m': dt.reportTokens.font.size.m,
      focus: 'var(--focus-light-bg)'
    }, 'windowshade-header')
  };
};
//# sourceMappingURL=BaseWindowShade.js.map